import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, MessageCircle, Wrench, Search, Package, RefreshCw, Star } from 'lucide-react';

const LandingPage = () => {
    const [currentTestimonial, setCurrentTestimonial] = useState(0);
    const [formData, setFormData] = useState({
        guid: '',
        name: '',
        email: '',
        question: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('');
    const [errors, setErrors] = useState({
        name: false,
        email: false,
        question: false
    });

    const [currentScreenshot, setCurrentScreenshot] = useState(0);

    const screenshots = [
        {
            src: `${process.env.PUBLIC_URL}/screenshots/login.jpg`,
            label: 'Login',
            description: 'Acesso rápido e seguro à sua conta'
        },
        {
            src: `${process.env.PUBLIC_URL}/screenshots/lista-produtos.jpg`,
            label: 'Busca de Equipamentos',
            description: 'Encontre equipamentos perto de você por localização'
        },
        {
            src: `${process.env.PUBLIC_URL}/screenshots/detalhe-produto.jpg`,
            label: 'Detalhes do Equipamento',
            description: 'Veja fotos, preço/dia e disponibilidade em tempo real'
        },
        {
            src: `${process.env.PUBLIC_URL}/screenshots/carrinho.jpg`,
            label: 'Carrinho',
            description: 'Escolha o período de locação e revise seu pedido'
        },
        {
            src: `${process.env.PUBLIC_URL}/screenshots/forma-pagamento.jpg`,
            label: 'Pagamento',
            description: 'Pague com cartão de forma rápida e segura'
        },
        {
            src: `${process.env.PUBLIC_URL}/screenshots/meus-pedidos.jpg`,
            label: 'Meus Pedidos',
            description: 'Acompanhe todos os seus aluguéis em um só lugar'
        },
        {
            src: `${process.env.PUBLIC_URL}/screenshots/chat.jpg`,
            label: 'Mensagens',
            description: 'Converse diretamente com locadores e clientes'
        },
        {
            src: `${process.env.PUBLIC_URL}/screenshots/resumo-pedidos-loja.jpg`,
            label: 'Resumo dos Pedidos (Lojista)',
            description: 'Gerencie todos os pedidos recebidos na sua loja'
        },
        {
            src: `${process.env.PUBLIC_URL}/screenshots/painel-financeiro.jpg`,
            label: 'Painel Financeiro',
            description: 'Acompanhe sua receita, ticket médio e taxa de conclusão'
        },
        {
            src: `${process.env.PUBLIC_URL}/screenshots/carteira.jpg`,
            label: 'Carteira',
            description: 'Visualize seu saldo, extrato e solicite saques'
        },
    ];

    const prevScreenshot = () =>
        setCurrentScreenshot((prev) => (prev === 0 ? screenshots.length - 1 : prev - 1));
    const nextScreenshot = () =>
        setCurrentScreenshot((prev) => (prev === screenshots.length - 1 ? 0 : prev + 1));

    // Dados dos depoimentos
    const testimonials = [
        {
            name: "João Silva",
            role: "Proprietário de Loja",
            comment: "O Locjah revolucionou meu negócio! Agora consigo gerenciar todos os meus equipamentos de forma digital e alcançar muito mais clientes.",
            rating: 5
        },
        {
            name: "Maria Santos",
            role: "Cliente",
            comment: "Precisava de uma furadeira para um projeto em casa. Com o Locjah foi super fácil encontrar e alugar. Entrega rápida e equipamento em perfeito estado!",
            rating: 5
        },
        {
            name: "Carlos Oliveira",
            role: "Construtor",
            comment: "Como trabalho com várias obras, o Locjah me ajuda a encontrar equipamentos específicos rapidamente. A variedade é incrível!",
            rating: 5
        },
        {
            name: "Ana Paula",
            role: "Proprietária de Equipamentos",
            comment: "Tenho uma pequena loja e o app me permitiu expandir meu alcance. Os clientes encontram meus equipamentos facilmente.",
            rating: 5
        }
    ];

    // Auto-rotação do carousel
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 12000);
        return () => clearInterval(interval);
    }, []);

    const nextTestimonial = () => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    };

    const prevTestimonial = () => {
        setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async () => {
        const newErrors = {
            name: !formData.name.trim(),
            email: !formData.email.trim(),
            question: !formData.question.trim()
        };

        setErrors(newErrors);

        if (Object.values(newErrors).some(error => error)) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('');

        try {
            // Substitua pela URL da sua API
            const response = await fetch(process.env.REACT_APP_API_URL || 'https://sua-api-aqui.com/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Locjah-Channel': 'web',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setSubmitStatus('success');
                setFormData({ name: '', email: '', message: '' });
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Erro ao enviar formulário:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const primaryColor = 'rgb(0, 10, 117)';

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
            {/* Header */}
            <header className="bg-white shadow-lg sticky top-0 z-50" role="banner">
                <div className="container mx-auto px-4 py-4 flex items-center justify-between">
                    <a href="/" className="flex items-center space-x-3" aria-label="Locjah - Página inicial">
                        <picture>
                            <source srcSet={`${process.env.PUBLIC_URL}/logo.webp`} type="image/webp" />
                            <img
                                src={`${process.env.PUBLIC_URL}/logo.png`}
                                alt="Locjah - Plataforma de locação de equipamentos"
                                className="w-12 h-12 rounded-xl"
                                width="48"
                                height="48"
                                fetchpriority="high"
                                decoding="async"
                            />
                        </picture>
                        <span className="text-2xl font-bold" style={{ color: primaryColor }}>
                            Locjah
                        </span>
                    </a>
                    <div className="flex items-center gap-4 md:gap-6">
                        <nav className="hidden md:flex space-x-6" aria-label="Navegação principal">
                            <a href="#como-funciona" className="text-gray-600 hover:text-blue-600 transition-colors">Como Funciona</a>
                            <a href="#depoimentos" className="text-gray-600 hover:text-blue-600 transition-colors">Depoimentos</a>
                            <a href="#contato" className="text-gray-600 hover:text-blue-600 transition-colors">Contato</a>
                        </nav>
                        <a
                            href="/lojistas"
                            className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm hover:shadow-md transition-shadow"
                            style={{ backgroundColor: primaryColor }}
                        >
                            Sou Lojista
                        </a>
                    </div>
                </div>
            </header>

            <main>
            {/* Hero Section */}
            <section className="relative overflow-hidden py-16 lg:py-24 px-4" aria-labelledby="hero-title">
                {/* Fundo decorativo */}
                <div className="absolute inset-0 -z-10" aria-hidden="true">
                    <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-blue-200/40 blur-3xl" />
                    <div className="absolute top-40 -right-24 w-96 h-96 rounded-full bg-blue-300/30 blur-3xl" />
                </div>

                <div className="container mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
                        {/* Texto + CTA */}
                        <div className="text-center lg:text-left">
                            <span className="inline-block text-sm font-semibold px-4 py-1.5 rounded-full mb-6 text-white" style={{ backgroundColor: primaryColor }}>
                                Marketplace de locação de equipamentos
                            </span>
                            <h1 id="hero-title" className="text-4xl sm:text-5xl font-bold mb-6 leading-tight" style={{ color: primaryColor }}>
                                Locação de Equipamentos
                                <span className="block text-blue-600">Rápida, Digital e Segura</span>
                            </h1>
                            <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                                O marketplace que conecta quem precisa de equipamentos com quem tem para alugar.
                                Para obras, reparos, reformas e atividades profissionais — cotação e pagamento na palma da mão.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <a
                                    href="#download"
                                    className="inline-flex items-center justify-center px-8 py-4 rounded-xl text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    Baixar o App
                                </a>
                                <a
                                    href="/lojistas"
                                    className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold text-lg border-2 transition-colors duration-300 hover:bg-blue-50"
                                    style={{ color: primaryColor, borderColor: primaryColor }}
                                >
                                    Sou Lojista
                                </a>
                            </div>
                        </div>

                        {/* Mascote */}
                        <div className="flex justify-center lg:justify-end">
                            <div className="relative w-full max-w-sm mt-24">
                                {/* Brilho de fundo (apenas atrás do card) */}
                                <div className="absolute inset-x-0 top-16 bottom-0 -z-10 bg-gradient-to-br from-blue-200/60 to-blue-100/30 blur-2xl rounded-[2.5rem]" aria-hidden="true" />
                                <div className="relative bg-white rounded-3xl shadow-2xl px-8 pb-8 pt-28 text-center border border-blue-100">
                                    {/* Mascote "espiando" por cima do card (imagem com fundo transparente) */}
                                    <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-40 h-52">
                                        <picture>
                                            <source srcSet={`${process.env.PUBLIC_URL}/mascote.webp`} type="image/webp" />
                                            <img
                                                src={`${process.env.PUBLIC_URL}/mascote.png`}
                                                alt="Zecão, o mascote do Locjah que ajuda você a encontrar equipamentos para locação"
                                                className="w-full h-full object-contain drop-shadow-xl"
                                                width="160"
                                                height="208"
                                                decoding="async"
                                            />
                                        </picture>
                                    </div>
                                    <h2 className="text-xl font-bold text-gray-800 mb-2">Conheça o Zecão!</h2>
                                    <p className="text-gray-600">Nosso mascote está aqui para te ajudar a encontrar o equipamento perfeito!</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Download / QR Codes */}
            <section id="download" className="py-16 px-4 bg-white" aria-labelledby="download-title">
                <div className="container mx-auto text-center">
                    <h2 id="download-title" className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: primaryColor }}>
                        Baixe o App Agora
                    </h2>
                    <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
                        Escaneie o QR Code ou clique para baixar — comece a alugar em minutos.
                    </p>
                    <div className="flex flex-col md:flex-row gap-8 justify-center items-center">
                                {/* Google Play QR Code */}
                                <a
                                    href="https://play.google.com/store/apps/details?id=br.com.locjah"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-center bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer"
                                >
                                    <div className="w-32 h-32 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center">
                                        <picture>
                                            <source srcSet={`${process.env.PUBLIC_URL}/qr-google-play.webp`} type="image/webp" />
                                            <img
                                                src={`${process.env.PUBLIC_URL}/qr-google-play.png`}
                                                alt="QR Code para baixar o app Locjah no Google Play"
                                                className="w-28 h-28 object-contain"
                                                width="112"
                                                height="112"
                                                loading="lazy"
                                                decoding="async"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.nextSibling.style.display = 'block';
                                                }}
                                            />
                                        </picture>
                                        <div
                                            className="w-28 h-28 bg-black opacity-80 hidden"
                                            style={{
                                                backgroundImage: `url("data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3cdefs%3e%3cpattern id='qr' patternUnits='userSpaceOnUse' width='10' height='10'%3e%3crect width='5' height='5' fill='black'/%3e%3crect x='5' y='5' width='5' height='5' fill='black'/%3e%3c/pattern%3e%3c/defs%3e%3crect width='100' height='100' fill='url(%23qr)'/%3e%3c/svg%3e")`,
                                                backgroundSize: 'cover'
                                            }}
                                        />
                                    </div>
                                    <p className="font-semibold text-gray-800 mb-2">Google Play</p>
                                    <p className="text-sm text-gray-600 mb-3">Escaneie ou clique para baixar</p>
                                    <div className="inline-block px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium">
                                        Baixar no Android
                                    </div>
                                </a>

                                {/* App Store QR Code */}
                                {/*<a*/}
                                {/*    href="https://apps.apple.com/app/seu-app-id"*/}
                                {/*    target="_blank"*/}
                                {/*    rel="noopener noreferrer"*/}
                                {/*    className="text-center bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer"*/}
                                {/*>*/}
                                {/*    <div className="w-32 h-32 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center">*/}
                                {/*        <img*/}
                                {/*            src={`${process.env.PUBLIC_URL}/qr-app-store.png`}*/}
                                {/*            alt="QR Code App Store"*/}
                                {/*            className="w-28 h-28 object-contain"*/}
                                {/*            onError={(e) => {*/}
                                {/*                e.target.style.display = 'none';*/}
                                {/*                e.target.nextSibling.style.display = 'block';*/}
                                {/*            }}*/}
                                {/*        />*/}
                                {/*        <div*/}
                                {/*            className="w-28 h-28 bg-black opacity-80 hidden"*/}
                                {/*            style={{*/}
                                {/*                backgroundImage: `url("data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3cdefs%3e%3cpattern id='qr2' patternUnits='userSpaceOnUse' width='8' height='8'%3e%3crect width='4' height='4' fill='black'/%3e%3crect x='4' y='4' width='4' height='4' fill='black'/%3e%3c/pattern%3e%3c/defs%3e%3crect width='100' height='100' fill='url(%23qr2)'/%3e%3c/svg%3e")`,*/}
                                {/*                backgroundSize: 'cover'*/}
                                {/*            }}*/}
                                {/*        />*/}
                                {/*    </div>*/}
                                {/*    <p className="font-semibold text-gray-800 mb-2">App Store</p>*/}
                                {/*    <p className="text-sm text-gray-600 mb-3">Escaneie ou clique para baixar</p>*/}
                                {/*    <div className="inline-block px-4 py-2 bg-black text-white rounded-lg text-sm font-medium">*/}
                                {/*        Baixar no iOS*/}
                                {/*    </div>*/}
                                {/*</a>*/}

                                {/* Web Site QR Code */}
                                <a
                                    href="https://app.locjah.com.br/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-center bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer"
                                >
                                    <div className="w-32 h-32 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center">
                                        <picture>
                                            <source srcSet={`${process.env.PUBLIC_URL}/qr-web.webp`} type="image/webp" />
                                            <img
                                                src={`${process.env.PUBLIC_URL}/qr-web.png`}
                                                alt="QR Code para acessar o Locjah pelo navegador web"
                                                className="w-28 h-28 object-contain"
                                                width="112"
                                                height="112"
                                                loading="lazy"
                                                decoding="async"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.nextSibling.style.display = 'block';
                                                }}
                                            />
                                        </picture>
                                        <div
                                            className="w-28 h-28 bg-blue-600 opacity-80 hidden"
                                            style={{
                                                backgroundImage: `url("data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3cdefs%3e%3cpattern id='qr3' patternUnits='userSpaceOnUse' width='9' height='9'%3e%3crect width='4' height='4' fill='white'/%3e%3crect x='5' y='5' width='4' height='4' fill='white'/%3e%3c/pattern%3e%3c/defs%3e%3crect width='100' height='100' fill='%23000A75'/%3e%3crect width='100' height='100' fill='url(%23qr3)'/%3e%3c/svg%3e")`,
                                                backgroundSize: 'cover'
                                            }}
                                        />
                                    </div>
                                    <p className="font-semibold text-gray-800 mb-2">Site Web</p>
                                    <p className="text-sm text-gray-600 mb-3">Escaneie ou clique para acessar</p>
                                    <div
                                        className="inline-block px-4 py-2 text-white rounded-lg text-sm font-medium"
                                        style={{ backgroundColor: primaryColor }}
                                    >
                                        Acessar no Navegador
                                    </div>
                                </a>
                            </div>
                    </div>
                </section>

            {/* Proposta de Valor */}
            <section className="py-20 bg-white" aria-labelledby="valor-title">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-14">
                        <h2 id="valor-title" className="text-4xl font-bold mb-4" style={{ color: primaryColor }}>
                            Por que escolher o Locjah?
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Mais visibilidade para quem aluga, mais facilidade para quem precisa
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto">
                        {/* Para Lojistas */}
                        <div className="rounded-3xl p-8 shadow-lg border border-blue-100 bg-gradient-to-br from-blue-50 to-white">
                            <h3 className="text-2xl font-bold mb-6 flex items-center gap-3" style={{ color: primaryColor }}>
                                <span className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg flex-shrink-0" style={{ backgroundColor: primaryColor }}>🏪</span>
                                Para Lojistas
                            </h3>
                            <ul className="space-y-3">
                                {[
                                    'Novo canal de vendas 100% digital',
                                    'Maior visibilidade dos seus equipamentos',
                                    'Acesso a clientes além da sua região',
                                    'Pedidos com pagamento já confirmado',
                                    'Menos tempo gasto com orçamentos manuais',
                                    'Aumento do volume de locações',
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-gray-700">
                                        <span className="mt-1 w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: primaryColor }}>✓</span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-6 p-4 rounded-2xl text-sm text-blue-800 bg-blue-100">
                                Ideal para lojas de aluguel, casas de material de construção, lojas de ferramentas e empresas de locação para obras e eventos.
                            </div>
                        </div>

                        {/* Para Clientes */}
                        <div className="rounded-3xl p-8 shadow-lg border border-blue-100 bg-gradient-to-br from-blue-50 to-white">
                            <h3 className="text-2xl font-bold mb-6 flex items-center gap-3" style={{ color: primaryColor }}>
                                <span className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg flex-shrink-0" style={{ backgroundColor: primaryColor }}>👷</span>
                                Para Clientes
                            </h3>
                            <ul className="space-y-3">
                                {[
                                    'Busca rápida por equipamentos próximos',
                                    'Lista ordenada por distância geográfica',
                                    'Comparação de preços e especificações',
                                    'Cotação online, sem precisar ligar ou ir até a loja',
                                    'Pagamento seguro com cartão de crédito',
                                    'Processo de locação simples e rápido',
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-gray-700">
                                        <span className="mt-1 w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: primaryColor }}>✓</span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-6 p-4 rounded-2xl text-sm text-blue-800 bg-blue-100">
                                Perfeito para pessoas físicas, pedreiros, eletricistas, encanadores, pequenas e médias empresas, escritórios e condomínios.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Como Funciona */}
            <section id="como-funciona" className="py-20 bg-white" aria-labelledby="como-funciona-title">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 id="como-funciona-title" className="text-4xl font-bold mb-4" style={{ color: primaryColor }}>
                            Como Funciona o Locjah
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Um processo simples e eficiente que conecta quem tem equipamentos com quem precisa deles
                        </p>
                    </div>

                    {/* Para Locadores */}
                    <div className="mb-20">
                        <h3 className="text-3xl font-bold text-center mb-12 text-blue-600">
                            Para Lojistas
                        </h3>
                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="text-center p-6 rounded-2xl bg-blue-50 hover:shadow-lg transition-shadow">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                                    <Package className="w-8 h-8 text-white" />
                                </div>
                                <h4 className="text-xl font-bold mb-3" style={{ color: primaryColor }}>1. Cadastre Equipamentos</h4>
                                <p className="text-gray-600">
                                    Adicione seus equipamentos no app com fotos, informações técnicas detalhadas e preço por dia de locação.
                                </p>
                            </div>

                            <div className="text-center p-6 rounded-2xl bg-blue-50 hover:shadow-lg transition-shadow">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                                    <MessageCircle className="w-8 h-8 text-white" />
                                </div>
                                <h4 className="text-xl font-bold mb-3" style={{ color: primaryColor }}>2. Receba Pedidos</h4>
                                <p className="text-gray-600">
                                    Receba pedidos com pagamento online já confirmado automaticamente — sem negociação manual de valores.
                                </p>
                            </div>

                            <div className="text-center p-6 rounded-2xl bg-blue-50 hover:shadow-lg transition-shadow">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                                    <Wrench className="w-8 h-8 text-white" />
                                </div>
                                <h4 className="text-xl font-bold mb-3" style={{ color: primaryColor }}>3. Entregue ou Disponibilize</h4>
                                <p className="text-gray-600">
                                    Separe o equipamento e faça a entrega no local ou disponibilize para retirada na sua loja.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Para Clientes */}
                    <div>
                        <h3 className="text-3xl font-bold text-center mb-12 text-blue-600">
                            Para Clientes
                        </h3>
                        <div className="grid md:grid-cols-4 gap-6">
                            <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-lg transition-shadow">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                                    <Search className="w-8 h-8 text-white" />
                                </div>
                                <h4 className="text-lg font-bold mb-3" style={{ color: primaryColor }}>1. Pesquise</h4>
                                <p className="text-gray-600 text-sm">
                                    Encontre o equipamento ideal para seu projeto usando nossos filtros avançados.
                                </p>
                            </div>

                            <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-lg transition-shadow">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                                    <MessageCircle className="w-8 h-8 text-white" />
                                </div>
                                <h4 className="text-lg font-bold mb-3" style={{ color: primaryColor }}>2. Pague Online</h4>
                                <p className="text-gray-600 text-sm">
                                    Finalize o pedido e pague com cartão de crédito de forma segura. O lojista é notificado automaticamente.
                                </p>
                            </div>

                            <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-lg transition-shadow">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                                    <Package className="w-8 h-8 text-white" />
                                </div>
                                <h4 className="text-lg font-bold mb-3" style={{ color: primaryColor }}>3. Retire/Receba</h4>
                                <p className="text-gray-600 text-sm">
                                    Retire na loja ou receba a entrega no local do seu projeto.
                                </p>
                            </div>

                            <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-lg transition-shadow">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                                    <RefreshCw className="w-8 h-8 text-white" />
                                </div>
                                <h4 className="text-lg font-bold mb-3" style={{ color: primaryColor }}>4. Devolva</h4>
                                <p className="text-gray-600 text-sm">
                                    Após o uso, devolva o equipamento conforme combinado.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Screenshots do App */}
            <section className="py-20 bg-gradient-to-b from-white to-blue-50" aria-label="Telas do aplicativo">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold mb-4" style={{ color: primaryColor }}>
                            Veja o App em Ação
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Uma experiência simples e intuitiva do início ao fim
                        </p>
                    </div>

                    <div className="flex flex-col items-center">
                        {/* Carrossel */}
                        <div className="relative flex items-center justify-center w-full max-w-xs sm:max-w-sm">
                            {/* Botão anterior */}
                            <button
                                onClick={prevScreenshot}
                                aria-label="Tela anterior"
                                className="absolute -left-4 sm:-left-8 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-lg text-white transition-opacity hover:opacity-80"
                                style={{ backgroundColor: primaryColor }}
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>

                            {/* Imagem com moldura de celular */}
                            <div className="relative mx-auto" style={{ width: '220px' }}>
                                {/* Moldura */}
                                <div
                                    className="relative rounded-3xl overflow-hidden shadow-2xl border-4"
                                    style={{ borderColor: primaryColor, aspectRatio: '9/19' }}
                                >
                                    <img
                                        src={screenshots[currentScreenshot].src}
                                        alt={screenshots[currentScreenshot].label}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                                {/* Notch decorativo */}
                                <div
                                    className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-4 rounded-b-xl z-10"
                                    style={{ backgroundColor: primaryColor }}
                                />
                            </div>

                            {/* Botão próximo */}
                            <button
                                onClick={nextScreenshot}
                                aria-label="Próxima tela"
                                className="absolute -right-4 sm:-right-8 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-lg text-white transition-opacity hover:opacity-80"
                                style={{ backgroundColor: primaryColor }}
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Legenda */}
                        <div className="mt-8 text-center">
                            <p className="text-lg font-bold" style={{ color: primaryColor }}>
                                {screenshots[currentScreenshot].label}
                            </p>
                            <p className="text-gray-500 mt-1 text-sm">
                                {screenshots[currentScreenshot].description}
                            </p>
                        </div>

                        {/* Indicadores (dots) */}
                        <div className="flex space-x-2 mt-6">
                            {screenshots.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentScreenshot(idx)}
                                    aria-label={`Ver tela ${idx + 1}`}
                                    className="w-2.5 h-2.5 rounded-full transition-all"
                                    style={{
                                        backgroundColor: idx === currentScreenshot ? primaryColor : '#CBD5E1',
                                        transform: idx === currentScreenshot ? 'scale(1.3)' : 'scale(1)'
                                    }}
                                />
                            ))}
                        </div>

                        {/* Miniaturas */}
                        <div className="flex space-x-3 mt-8 overflow-x-auto pb-2 max-w-full px-4">
                            {screenshots.map((shot, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentScreenshot(idx)}
                                    aria-label={shot.label}
                                    className="flex-shrink-0 rounded-xl overflow-hidden transition-all"
                                    style={{
                                        width: '52px',
                                        height: '92px',
                                        border: idx === currentScreenshot ? `3px solid ${primaryColor}` : '3px solid transparent',
                                        opacity: idx === currentScreenshot ? 1 : 0.55
                                    }}
                                >
                                    <img
                                        src={shot.src}
                                        alt={shot.label}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Depoimentos */}
            <section id="depoimentos" className="py-20 bg-gradient-to-r from-blue-50 to-blue-100" aria-labelledby="depoimentos-title">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 id="depoimentos-title" className="text-4xl font-bold mb-4" style={{ color: primaryColor }}>
                            O que nossos usuários dizem
                        </h2>
                        <p className="text-xl text-gray-600">
                            Experiências reais de quem já usa o Locjah
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white rounded-3xl shadow-2xl p-8 relative">
                            <div className="flex items-center justify-between mb-6">
                                <button
                                    onClick={prevTestimonial}
                                    aria-label="Depoimento anterior"
                                    className="p-3 rounded-full hover:bg-blue-50 transition-colors"
                                    style={{ color: primaryColor }}
                                >
                                    <ChevronLeft className="w-6 h-6" />
                                </button>

                                <div className="text-center flex-1">
                                    {testimonials.map((testimonial, index) => (
                                        <article
                                            key={testimonial.name}
                                            className={index === currentTestimonial ? 'block' : 'hidden'}
                                            aria-hidden={index !== currentTestimonial}
                                        >
                                            <div className="flex justify-center mb-4">
                                                {[...Array(testimonial.rating)].map((_, i) => (
                                                    <Star key={i} className="w-6 h-6 text-yellow-400 fill-current" />
                                                ))}
                                            </div>
                                            <p className="text-lg text-gray-700 mb-6 italic leading-relaxed">
                                                "{testimonial.comment}"
                                            </p>
                                            <div>
                                                <h4 className="font-bold text-lg" style={{ color: primaryColor }}>
                                                    {testimonial.name}
                                                </h4>
                                                <p className="text-gray-500">{testimonial.role}</p>
                                            </div>
                                        </article>
                                    ))}
                                </div>

                                <button
                                    onClick={nextTestimonial}
                                    aria-label="Próximo depoimento"
                                    className="p-3 rounded-full hover:bg-blue-50 transition-colors"
                                    style={{ color: primaryColor }}
                                >
                                    <ChevronRight className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="flex justify-center space-x-2">
                                {testimonials.map((testimonial, index) => (
                                    <button
                                        key={testimonial.name}
                                        onClick={() => setCurrentTestimonial(index)}
                                        aria-label={`Ver depoimento ${index + 1} de ${testimonials.length}`}
                                        className={`w-3 h-3 rounded-full transition-colors ${
                                            index === currentTestimonial ? 'bg-blue-600' : 'bg-gray-300'
                                        }`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Formulário de Contato */}
            <section id="contato" className="py-20 bg-white" aria-labelledby="contato-title">
                <div className="container mx-auto px-4">
                    <div className="max-w-2xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 id="contato-title" className="text-4xl font-bold mb-4" style={{ color: primaryColor }}>
                                Tem alguma dúvida?
                            </h2>
                            <p className="text-xl text-gray-600">
                                Entre em contato conosco e nossa equipe responderá rapidamente
                            </p>
                        </div>

                        <div className="bg-blue-50 rounded-3xl p-8 shadow-lg">
                            <div className="mb-6">
                                <label className="block text-gray-700 font-semibold mb-2">
                                    Nome Completo
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className={`w-full px-4 py-3 rounded-xl border ${
                                        errors.name ? 'border-red-500' : 'border-gray-300'
                                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="Seu nome completo"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-red-500 text-sm">Nome é obrigatório</p>
                                )}
                            </div>

                            <div className="mb-6">
                                <label className="block text-gray-700 font-semibold mb-2">
                                    E-mail
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className={`w-full px-4 py-3 rounded-xl border ${
                                        errors.email ? 'border-red-500' : 'border-gray-300'
                                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="seu@email.com"
                                />
                                {errors.email && (
                                    <p className="mt-1 text-red-500 text-sm">E-mail é obrigatório</p>
                                )}
                            </div>

                            <div className="mb-6">
                                <label className="block text-gray-700 font-semibold mb-2">
                                    Sua Dúvida
                                </label>
                                <textarea
                                    name="question"
                                    value={formData.question}
                                    onChange={handleInputChange}
                                    required
                                    rows={5}
                                    className={`w-full px-4 py-3 rounded-xl border ${
                                        errors.question ? 'border-red-500' : 'border-gray-300'
                                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none`}
                                    placeholder="Descreva sua dúvida ou sugestão..."
                                />
                                {errors.question && (
                                    <p className="mt-1 text-red-500 text-sm">Mensagem é obrigatória</p>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="w-full py-4 rounded-xl text-white font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: primaryColor }}
                            >
                                {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
                            </button>

                            {submitStatus === 'success' && (
                                <div className="mt-4 p-4 bg-green-100 text-green-700 rounded-xl text-center">
                                    Mensagem enviada com sucesso! Entraremos em contato em breve.
                                </div>
                            )}

                            {submitStatus === 'error' && (
                                <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-xl text-center">
                                    Erro ao enviar mensagem. Tente novamente.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
            </main>

            {/* Footer */}
            <footer className="py-12" style={{ backgroundColor: primaryColor }} role="contentinfo">
                <div className="container mx-auto px-4 text-center text-white">
                    <div className="flex items-center justify-center space-x-3 mb-6">
                        <picture>
                            <source srcSet={`${process.env.PUBLIC_URL}/logo.webp`} type="image/webp" />
                            <img
                                src={`${process.env.PUBLIC_URL}/logo.png`}
                                alt="Locjah - Locação de equipamentos"
                                className="w-10 h-10 rounded-lg bg-white p-1"
                                width="40"
                                height="40"
                                loading="lazy"
                                decoding="async"
                            />
                        </picture>
                        <h3 className="text-2xl font-bold">Locjah</h3>
                    </div>
                    <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                        O marketplace que conecta quem precisa de equipamentos com quem tem para alugar —
                        de forma rápida, digital e segura.
                    </p>
                    <div className="flex items-center justify-center space-x-6 mb-6">
                        <a
                            href="https://www.instagram.com/contato.locjah/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram Locjah"
                            className="text-blue-100 hover:text-white transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                            </svg>
                        </a>
                        <a
                            href="mailto:comercial@locjah.com.br"
                            aria-label="E-mail Locjah"
                            className="text-blue-100 hover:text-white transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                            </svg>
                        </a>
                        <a
                            href="https://wa.me/5511944852425"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="WhatsApp Locjah"
                            className="text-blue-100 hover:text-white transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                        </a>
                    </div>
                    <div className="mt-8 pt-8 border-t border-blue-800 text-blue-200 text-sm">
                        © 2026 Locjah — uma marca de Zenith Tecnologia e Desenvolvimento LTDA. Todos os direitos reservados.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
